import {
  findNodePath,
  getAboveNode,
  PlateEditor,
  PlateRenderElementProps,
  setNodes,
  TEditor,
  TElement,
  usePlateEditorRef,
} from '@udecode/plate';
import cn from 'classnames';
import * as React from 'react';
import { ReactEditor } from 'slate-react';

const { useState, useEffect, useRef, useMemo } = React;

export const ColumnElement = ({
  attributes,
  children,
  nodeProps,
  element,
}: PlateRenderElementProps & { element: TElement & { width: string | number } }): JSX.Element => {
  const [isResizing, setIsResizing] = useState(false);
  const resizeBarRef = useRef<HTMLDivElement>(null);
  const [original, setOriginal] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [columnWidths, setColumnWidths] = useState<number[]>([]);
  const editor = usePlateEditorRef() as TEditor & ReactEditor & PlateEditor;

  const shouldDisplayResize = useMemo(() => {
    const currentPath = findNodePath(editor, element);
    const aboveEntry = getAboveNode(editor, { at: currentPath });
    if (aboveEntry) {
      const [node] = aboveEntry;
      if (node.children.length - 1 === currentPath?.[1]) {
        return false;
      }
    }
    return true;
  }, [element]);

  useEffect(() => {
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseleave', onMouseUp);
    return () => {
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseleave', onMouseUp);
    };
  }, [isResizing, element, original, columnWidths]);

  const getMultiColElementWidth = () => {
    const multiColElem = resizeBarRef.current?.parentElement?.parentElement;
    if (!multiColElem) return;
    const size = {
      width: multiColElem.offsetWidth,
    };
    return size;
  };

  const calculateCurrentColumnNewWidth = (clientX: number) => {
    let newWidth = original.width;
    newWidth = original.width + ((clientX - original.x) * 1) / 1;
    return newWidth;
  };

  const currentColPercentage = (width: number) => {
    const parentSize = getMultiColElementWidth();
    if (!parentSize) return 0;

    return getNumberBetweenMinMax((width / parentSize?.width) * 100);
  };

  const onMouseUp = () => {
    if (!isResizing) return;
    const currentPath = findNodePath(editor, element);
    if (!currentPath) return;
    columnWidths.map((width, index) => {
      setNodes(editor, { width: `${width}%` }, { at: [currentPath[0], index] });
    });
    setIsResizing(false);
  };

  const onMouseMove = (event: MouseEvent) => {
    const multiColElem = resizeBarRef.current?.parentElement?.parentElement;
    const colElem = resizeBarRef.current?.parentElement;
    if (!isResizing) return;
    if (!multiColElem) return;

    const { clientX } = event;

    let newWidth = calculateCurrentColumnNewWidth(clientX) as string | number;

    if (typeof newWidth === 'number') {
      newWidth = currentColPercentage(newWidth);
    }

    const newWidths: number[] = [];
    let currentColumnIndex = 0;

    multiColElem.childNodes.forEach((node: HTMLDivElement, index) => {
      if (colElem && node.isEqualNode(colElem)) {
        currentColumnIndex = index;
      }
    });

    let totalWidthWithoutRightColumn = 0;

    multiColElem.childNodes.forEach((node: HTMLDivElement, index) => {
      if (index === currentColumnIndex && typeof newWidth === 'number') {
        totalWidthWithoutRightColumn += newWidth;
        newWidths.push(newWidth);
      } else {
        const percent = currentColPercentage(node.clientWidth);
        if (currentColumnIndex + 1 !== index) {
          totalWidthWithoutRightColumn += percent;
        }
        newWidths.push(percent);
      }
    });

    newWidths[currentColumnIndex + 1] = 100 - totalWidthWithoutRightColumn;

    const childNodes = multiColElem.childNodes as NodeListOf<HTMLElement>;
    newWidths.map((width, index) => {
      childNodes[index].style.width = `${width}%`;
    });
    setColumnWidths(newWidths);
  };

  const handleMouseDown: React.MouseEventHandler<HTMLDivElement> = (event) => {
    const colElem = resizeBarRef.current?.parentElement;
    if (!colElem) return;
    const { clientX, clientY } = event;
    setOriginal((s) => ({
      ...s,
      width: colElem.offsetWidth,
      x: clientX,
      y: clientY,
    }));
    setIsResizing(true);
  };

  return (
    <div
      {...attributes}
      className={cn('flex', { ['select-none']: isResizing })}
      style={{ width: element.width }}
    >
      <div
        {...nodeProps}
        contentEditable={true}
        suppressContentEditableWarning={true}
        style={{ width: `calc(100% - ${shouldDisplayResize ? 5 : 0}px)` }}
        className="w-full"
      >
        {children}
      </div>
      {shouldDisplayResize && (
        <div
          contentEditable={false}
          className="h-full w-5 bg-gray-200 opacity-0 hover:opacity-100"
          onMouseDown={handleMouseDown}
          style={{ cursor: 'col-resize' }}
          ref={resizeBarRef}
        />
      )}
    </div>
  );
};

function getNumberBetweenMinMax(number: number, min = 10, max = 100) {
  return number < min ? min : number > max ? max : number;
}
